import { Colors } from '@oy/uicore';
import { SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React, { FC } from 'react';
import ReactDatePicker, { DatePickerProps } from 'react-datepicker';
import { useUITranslate } from '../../../../hooks/useUITranslate';
import { formatString, makeId } from '../../../../utilities/idComponent';
import {
  getCustomMessageCSSProperties,
  getMessageIconUrl,
} from '../../../../utilities/inputMessage';
import { FieldContainer, FieldCustomMessage } from '../../FieldContainer';
import styles from './DatePicker.module.css';

type FortuneDatePickerProps = {
  boldLabel?: boolean;
  customMessage?: FieldCustomMessage | null;
  errorMessage?: string;
  errorMessageId?: string;
  helpText?: string;
  isHidden?: boolean;
  isHorizontal?: boolean;
  label?: string | React.ReactNode;
  leftColLength?: number;
  noIcon?: boolean;
  requiredSign?: boolean;
  shouldUseMessageColor?: boolean;
  warningMessage?: string;
} & DatePickerProps;

const DatePicker: FC<FortuneDatePickerProps> = ({
  boldLabel,
  customInput,
  customMessage,
  disabled,
  errorMessage,
  errorMessageId,
  helpText,
  id,
  label,
  leftColLength,
  locale,
  maxDate,
  noIcon,
  placeholderText,
  requiredSign,
  selected,
  shouldUseMessageColor,
  warningMessage,
  dateFormat = 'dd MMM yyyy',
  isHidden = false,
  isHorizontal = false,
  minDate = new Date(),
  portalId = 'root',
  ...rest
}: FortuneDatePickerProps) => {
  const { translate, lang } = useUITranslate();

  const getMessageStyle = () => {
    if (errorMessage) {
      return styles.errorInput;
    } else if (warningMessage) {
      return styles.warningInput;
    } else if (customMessage) {
      return styles.customMessageInput;
    }
    return '';
  };

  const getMessageIcon = () => {
    let icon = '';

    if (noIcon) return '';

    if (errorMessage) {
      icon = getMessageIconUrl('error', Colors.RED100);
    } else if (customMessage && customMessage.icon) {
      icon = getMessageIconUrl(customMessage.icon, customMessage.color);
    }
    return icon ? `url("${icon}")` : '';
  };

  const showMessageIcon = () => {
    return !!errorMessage || !!customMessage?.icon;
  };

  return (
    <FieldContainer
      label={label}
      shouldUseMessageColor={shouldUseMessageColor}
      helpText={helpText}
      isHorizontal={isHorizontal}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      customMessage={customMessage}
      isRequired={requiredSign}
      errorMessageId={errorMessageId}
      leftColLength={leftColLength}
      boldLabel={boldLabel}
    >
      <div
        id={makeId('datePicker', formatString(label ?? placeholderText), id)}
        className={classNames(styles.container, {
          [styles.hideDateInput]: isHidden,
        })}
        style={
          {
            '--message-icon': getMessageIcon(),
            ...getCustomMessageCSSProperties(customMessage),
          } as React.CSSProperties
        }
      >
        {/* @ts-ignore */}
        <ReactDatePicker
          selectsStart
          disabled={disabled}
          isClearable={!disabled && (!!selected || !!customInput)}
          className={classNames(
            styles.inputContainer,
            'font-body-MdRegular',
            getMessageStyle(),
            {
              [styles.inputWithMessageIcon]: showMessageIcon(),
            },
          )}
          dateFormat={dateFormat}
          customInput={customInput}
          placeholderText={placeholderText || translate('Select Date')}
          selected={selected}
          minDate={minDate}
          maxDate={maxDate}
          portalId={portalId}
          locale={locale ?? lang}
          {...rest}
        />
        {!selected && !customInput && !noIcon && (
          <div className={styles.dateIcon}>
            <SystemIcon name="calendar" size={20} color={Colors.BLACK100} />
          </div>
        )}
      </div>
    </FieldContainer>
  );
};

export default DatePicker;
